/**
 * Playable character presets.
 *
 * Primary skinned model: fencer-rigged.glb — the CC-BY 3dUVpro fencer mesh
 * with a Mixamo-style skeleton + Idle/Walk/Run + combat clips (Blender rig).
 * Fallback: three.js Soldier.glb. Procedural CombatClips fill any gaps (EnGarde).
 */

export type CharacterId = 'fencer';

export interface CharacterPreset {
  id: CharacterId;
  label: string;
  description: string;
  preferredUrl: string;
  fallbackUrl: string;
  thumbUrl: string;
  creditUrl: string;
  defaultColor: 'blue' | 'red' | 'gold' | 'green' | 'purple' | 'cyan' | 'white' | 'black';
  targetHeight: number;
  modelYaw: number;
  animIdle: string[];
  animWalk: string[];
  animRun: string[];
  /** Optional name lists for combat clips embedded in the GLB */
  animLunge?: string[];
  animAttack?: string[];
  animParryHigh?: string[];
  animParryLow?: string[];
  animFleche?: string[];
  animEnGarde?: string[];
  weaponHand: 'RightHand' | 'LeftHand';
  unskinned?: boolean;
  weaponOffset?: [number, number, number];
}

/** Runtime override from Game Editor publish / cloud active asset */
export let runtimeModelUrlOverride: string | null = null;

export function setRuntimeModelUrl(url: string | null): void {
  runtimeModelUrlOverride = url;
}

export function getPreferredModelUrl(preset: CharacterPreset): string {
  return runtimeModelUrlOverride || preset.preferredUrl;
}

export const CHARACTERS: Record<CharacterId, CharacterPreset> = {
  fencer: {
    id: 'fencer',
    label: 'Fencer',
    description: 'Skinned fencer — walk, lunge & sword combat animations',
    preferredUrl: '/models/fencer-rigged.glb',
    fallbackUrl: '/models/soldier.glb',
    thumbUrl: '/models/fencer-funde-thumb.jpg',
    creditUrl: 'https://sketchfab.com/3dUVpro',
    defaultColor: 'blue',
    targetHeight: 1.78,
    modelYaw: 0,
    animIdle: ['Idle', 'idle', 'TPose'],
    animWalk: ['Walk', 'walk', 'Walking'],
    animRun: ['Run', 'run', 'Running'],
    animLunge: ['Lunge', 'Combat_Lunge', 'lunge'],
    animAttack: ['Attack', 'Combat_Attack', 'attack'],
    animParryHigh: ['ParryHigh', 'Combat_ParryHigh', 'parry'],
    animParryLow: ['ParryLow', 'Combat_ParryLow'],
    animFleche: ['Fleche', 'Combat_Fleche', 'fleche'],
    animEnGarde: ['EnGarde', 'Combat_EnGarde', 'en_garde', 'engarde'],
    weaponHand: 'RightHand',
    unskinned: false,
  },
};

export const CHARACTER_LIST: CharacterPreset[] = [CHARACTERS.fencer];
